// useFileHandler.js is a custom React hook that handles uploading files to the server.
import { useState, useCallback } from 'react';
import { getUploadUrl } from '../../config/endpoints';
import { 
    getCloudProvider, 
    CLOUD_PROVIDERS, 
    isAzureProvider,
    isGCPProvider 
} from '../../config/cloudProvider';  

const useFileHandler = (sendMessage, updateChatMessage) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileDesignation, setFileDesignation] = useState('');
    const [fileAttachedMessage, setFileAttachedMessage] = useState('');
    const [isSendButtonEnabled, setIsSendButtonEnabled] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');    
    const [isUploading, setIsUploading] = useState(false);

    // List of allowed file types
    const allowedTypes = [ //26 types
        'application/pdf',                                              // pdf
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
        'application/msword',                                           // doc
        'image/jpeg',                                                   // jpg, jpeg
        'image/png',                                                    // png
        'text/plain',                                                   // txt
        'application/vnd.ms-excel',                                     // xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
        'text/csv',                                                     // csv
        'application/vnd.ms-powerpoint',                                // ppt
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
        'image/gif',                                                    // gif
        'image/bmp',                                                    // bmp
        'audio/mpeg',                                                   // mpeg
        'audio/wav',                                                    // wav
        'audio/x-wav',                                                  // x-wav
        'audio/wave',                                                   // wave
        'audio/aac',                                                    // aac
        'audio/flac',                                                   // flac
        'audio/ogg',                                                    // ogg
        'audio/x-aiff',                                                 // x-aiff
        'audio/mp3',                                                    // mp3
        'audio/m4a',                                                    // m4a
        'audio/x-m4a',                                                  // M4A Audio
        'audio/x-ms-wma',                                               // x-ms-wma
        'audio/vnd.wav',                                                // vnd.wav
        'audio/raw',                                                    // raw
        'image/RAW',                                                    // raw
        'application/rtf',                                              // rtf
    ];    

    const audioTypes = [
        'audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/aac', 'audio/flac',
        'audio/ogg', 'audio/x-aiff', 'audio/mp3', 'audio/m4a', 'audio/x-m4a', 'audio/x-ms-wma', 
        'audio/vnd.wav', 'audio/raw', 'image/RAW'
    ];
    
    function handleFileSelected(event) {
        console.log("File selected event triggered", event);
    
        if (!event || !event.target || !event.target.files || event.target.files.length === 0) {
            console.error("No file selected or event is not defined properly");
            return;
        }
    
        const file = event.target.files[0];
    
        if (!file) {
            console.error("Selected file is undefined");
            setSelectedFile(null);
            setIsSendButtonEnabled(false);
            setFileAttachedMessage('No file selected.');
            return;
        }
    
        console.log("Selected file:", file);
        console.log("File MIME type:", file.type);
    
        if (file && allowedTypes.includes(file.type)) {
            console.log("File is allowed and selected:", file.name);
            setSelectedFile(file);
            setIsSendButtonEnabled(true);
            setFileAttachedMessage(`${file.name} ready to send.`);
        } else {
            console.log("Invalid file type or no file selected");
            setSelectedFile(null);
            setIsSendButtonEnabled(false);
            setFileAttachedMessage('Invalid file type. Allowed types are: ' + allowedTypes.join(', '));
        }
    }    
    
    const handleRemoveFile = () => {
        console.log("file remove function triggered");
        setSelectedFile(null);
        setIsUploading(false);
        setUploadMessage('');
        setIsSendButtonEnabled(false);
        console.log("Send button should be disabled now");
    };    

    const uploadFile = useCallback(async (fileDesignationParam) => {
        console.log("uploadFile function triggered");
        
        if (!selectedFile) {
            console.log("No file to upload, exiting uploadFile");
            return;
        }

        const uploadMessageId = sendMessage(`Uploading ${selectedFile.name}...`, 'user');
        setIsUploading(true);

        try {
            const provider = getCloudProvider();
            const useAzure = isAzureProvider();
            
            const uploadEndpoint = getUploadUrl(provider);
            console.log(`Using ${provider} 1. Intitial endpoint: ${uploadEndpoint}`);

            // Handle URL construction differently for each provider
            let requestUrl;
            if (useAzure) {
                const urlWithParams = new URL(uploadEndpoint);
                urlWithParams.searchParams.append('filename', selectedFile.name);
                if (fileDesignationParam) {
                    urlWithParams.searchParams.append('fileDesignation', fileDesignationParam);
                }
                requestUrl = urlWithParams.toString();
            } else {
                console.log('2. Building GCP URL...');
                requestUrl = `${uploadEndpoint}?filename=${encodeURIComponent(selectedFile.name)}`;
                console.log('3. GCP URL before fileDesignation:', requestUrl);
                if (fileDesignationParam) {
                    requestUrl += `&fileDesignation=${fileDesignationParam}`;
                }
                console.log('4. Final GCP URL:', requestUrl);
            }
            
            console.log('5. URL immediately before fetch:', requestUrl);
            const signedUrlResponse = await fetch(requestUrl);

            if (!signedUrlResponse.ok) {
                const errorText = await signedUrlResponse.text();
                console.error('Failed to get signed URL:', errorText);
                throw new Error(`Failed to get signed URL: ${errorText}`);
            }

            const signedUrlData = await signedUrlResponse.json();
            console.log("Received signed URL data:", signedUrlData);

            const uploadUrl = signedUrlData.signed_url;
            
            if (!uploadUrl) {
                throw new Error('No valid upload URL received');
            }

            const storageResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: selectedFile,
                headers: {
                    'Content-Type': selectedFile.type,
                    ...(useAzure && { 'x-ms-blob-type': 'BlockBlob' })
                },
            });

            if (!storageResponse.ok) {
                const errorText = await storageResponse.text();
                console.error('Storage upload failed:', errorText);
                throw new Error(`Upload to ${useAzure ? 'Azure' : 'GCP'} storage failed: ${errorText}`);
            }

            console.log(`Upload successful to ${useAzure ? 'Azure Blob Storage' : 'Google Cloud Storage'}`);
            updateChatMessage(uploadMessageId, selectedFile.name);
            setUploadMessage('');

            const isAudioFile = audioTypes.includes(selectedFile.type);
            const messageData = isAudioFile ? {
                transcribeUrl: signedUrlData.transcribe_url
            } : {};

            sendMessage('File uploaded successfully!', 'maia', messageData);

            const returnObject = {
                file_id: signedUrlData.file_id,
                file_name: selectedFile.name,
                blob_name: signedUrlData.blob_name,
                file_url: useAzure ? signedUrlData.file_url.split('?')[0] : signedUrlData.file_url
            };

            console.log("Object to be returned:", returnObject);
            return returnObject;

        } catch (error) {
            console.error("Error during file upload:", error);
            setUploadMessage('Error during upload. Please try again.');
            updateChatMessage(uploadMessageId, 'Error during upload. Please try again.');
            throw error;
        } finally {
            setIsUploading(false);
            setSelectedFile(null);
            setFileAttachedMessage('');
            setIsSendButtonEnabled(false);
            setFileDesignation(null);
        }
    }, [selectedFile, sendMessage, updateChatMessage]);

    return {
        selectedFile,
        setSelectedFile,
        isSendButtonEnabled,
        setIsSendButtonEnabled,
        handleRemoveFile, 
        isUploading,
        setIsUploading,
        fileAttachedMessage,
        setFileAttachedMessage,
        uploadMessage,
        setUploadMessage,
        handleFileSelected,
        uploadFile, 
        setFileDesignation,
    };
};

export default useFileHandler;